import { Location } from '@angular/common'
import { Component, inject, OnInit, signal } from '@angular/core'
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { groupBy, GroupResult } from '@progress/kendo-data-query'
import { switchMap } from 'rxjs'

import { Tool } from '@cyborg-monorepo/hunter/common'
import {
  categories,
  CCM_CATEGORIES,
  CCM_FIELDS,
  Environment,
  fields,
  toolFactory,
  ToolMapping,
  toolMappings,
} from '@cyborg-monorepo/hunter/matchboi'
import { BreadcrumbItem, SnackbarService } from '@hyperkit'

import { BreadcrumbService } from '@hunt/services'

import { getToolIcon, supportedToolUrl } from '../../../../../helpers/utils'
import { EnvironmentService } from '../../../../shared/services/environment.service'
import { ToolService } from '../../../../shared/services/tool.service'

@Component({
  standalone: false,
  templateUrl: './tool.component.html',
  styleUrls: ['./tool.component.scss'],
})
export class ToolComponent implements OnInit {
  private route = inject(ActivatedRoute)
  private toolService = inject(ToolService)
  private environmentService = inject(EnvironmentService)
  private router = inject(Router)
  private formBuilder = inject(FormBuilder)
  private location = inject(Location)
  private breadcrumb = inject(BreadcrumbService)

  public isEditing = false
  public toolForm: FormGroup
  public tool: Tool
  public customMapping = false
  public getToolIcon = getToolIcon
  public supportedToolUrl = supportedToolUrl
  public breadcrumbs = signal<(BreadcrumbItem & { step: 'env' | 'tool' })[]>([])
  public data: Array<ToolMapping> = toolMappings
  public groupedData: GroupResult[] = groupBy(this.data, [
    { field: 'category' },
  ]) as GroupResult[]
  public categories: CCM_CATEGORIES = categories
  public categoryKeys = Object.keys(categories)
  public fields: CCM_FIELDS = fields
  public newTool = false
  public environment: Environment
  public categoryData = []
  public openIndexes: Set<number> = new Set()
  public readonly snack = inject(SnackbarService)

  // ---------------------------------------
  // LIFECYCLE
  // ---------------------------------------
  ngOnInit(): void {
    this.initializeEmptyForm()

    this.route.params
      .pipe(
        switchMap((params: Params) => {
          if (params.toolId) {
            this.newTool = false
            return this.toolService.getToolById(params.toolId)
          }
          this.newTool = true
          return this.environmentService.getEnvironmentById(
            params.environmentId,
          )
        }),
      )
      .subscribe((result: Tool | Environment) => {
        if (this.isTool(result)) {
          this.initializeFromTool(result as Tool)
        } else {
          this.initializeFromEnvironment(result as Environment)
        }
      })
  }

  // ---------------------------------------
  // FORM INITIALIZATION
  // ---------------------------------------
  private initializeEmptyForm(): void {
    this.toolForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
      product: new FormControl('', [Validators.required]),
      rootUrl: new FormControl(''),
      fieldMappings: this.formBuilder.array([]),
    })
  }

  private initializeFromEnvironment(env: Environment): void {
    this.environment = env
    this.breadcrumbs.set(
      this.breadcrumb.buildToolBreadcrumbs(this.environment.name, 'New Tool'),
    )
  }

  private initializeFromTool(tool: Tool): void {
    this.tool = tool

    this.breadcrumbs.set(
      this.breadcrumb.buildToolBreadcrumbs(
        this.tool.environment.name,
        this.tool.name,
      ),
    )

    this.toolForm.patchValue({
      name: tool.name,
      product: tool.product,
      rootUrl: tool.rootUrl,
    })

    // rebuild mappings cleanly
    const mappings = this.fieldMappings()
    mappings.clear()
    this.buildMappingsFromTool(tool)

    this.applyCategoryData(tool.product)
  }

  // ---------------------------------------
  // MAPPING CREATION
  // ---------------------------------------
  private buildMappingsFromTool(tool: Tool): void {
    tool.fieldMappings.forEach((mapping) => {
      const group = this.formBuilder.group({
        name: new FormControl(mapping.name, [Validators.required]),
        category: new FormControl(mapping.category, [Validators.required]),
        fields: this.formBuilder.array([]),
        toolSpecific: this.formBuilder.array([]),
      })

      const toolSpec = group.get('toolSpecific') as FormArray
      if (mapping.toolSpecific) {
        mapping.toolSpecific.forEach((field) => {
          toolSpec.push(
            this.formBuilder.group({
              [Object.keys(field)[0]]: new FormControl(
                Object.values(field)[0],
                Validators.required,
              ),
            }),
          )
        })
      }

      const fieldsArr = group.get('fields') as FormArray
      this.categories[mapping.category].ccm_fields.forEach((field) => {
        const mapped = mapping.fields.find((m) => Object.keys(m)[0] === field)
        fieldsArr.push(
          this.formBuilder.group({
            [field]: mapped ? Object.values(mapped)[0] : '',
          }),
        )
      })

      this.fieldMappings().push(group)
    })
  }

  // ---------------------------------------
  // FORM HELPERS
  // ---------------------------------------
  public fieldMappings(): FormArray {
    return this.toolForm.get('fieldMappings') as FormArray
  }

  public addFieldMapping(): void {
    const newIndex = this.fieldMappings().length
    const group = this.newFieldMapping()
    this.fieldMappings().push(group)
    this.openIndexes.clear()
    this.openIndexes.add(newIndex)
  }

  public newFieldMapping(): FormGroup {
    const group = this.formBuilder.group({
      name: new FormControl('', [Validators.required]),
      category: new FormControl('', [Validators.required]),
      fields: this.formBuilder.array([]),
      toolSpecific: this.formBuilder.array([]),
    })

    const selectedTool = this.data.find(
      (t) => t.name === this.toolForm.value.product,
    )
    const customFields = selectedTool?.toolSpecific

    if (customFields) {
      const toolSpec = group.get('toolSpecific') as FormArray
      customFields.forEach((field) => {
        toolSpec.push(
          this.formBuilder.group({
            [field]: new FormControl('', Validators.required),
          }),
        )
      })
    }

    return group
  }

  public removeFieldMapping(i: number): void {
    this.fieldMappings().removeAt(i)
  }

  // ---------------------------------------
  // CATEGORY / TOOL CHANGE HANDLING
  // ---------------------------------------
  private applyCategoryData(product: string): void {
    const tool = this.data.find((t) => t.name === product)
    if (!tool) return

    this.customMapping = tool.customMapping
    this.categoryData = tool.allowedCategories?.length
      ? tool.allowedCategories.map((c) => ({
          category: c,
          name: categories[c].name,
        }))
      : Object.entries(categories).map(([key, val]) => ({
          category: key,
          name: val.name,
        }))
  }

  public valueChange(event: any): void {
    this.applyCategoryData(event)
    this.fieldMappings().clear()
  }

  // ---------------------------------------
  // SAVE / DELETE
  // ---------------------------------------
  public saveTool(): void {
    if (!this.toolForm.valid) return

    if (this.newTool) {
      const tool: Tool = this.toolForm.value
      tool.environment = this.environment

      this.toolService.createTool(tool).subscribe({
        next: (tool: Tool) => {
          this.tool = tool
          this.newTool = false
          const currentUrl = this.location.path()
          const newUrl = `${currentUrl}/${tool.id}`
          this.location.replaceState(newUrl)
          this.snack.show('Changes saved', 5000)
        },
        error: () => {
          this.snack.show('Failed to save changes', 5000)
        },
      })
    } else {
      this.toolService.updateTool(this.tool.id, this.toolForm.value).subscribe({
        next: (tool: Tool) => {
          this.tool = tool
          this.snack.show('Changes saved', 5000)
        },
        error: () => {
          this.snack.show('Failed to save changes', 5000)
        },
      })
    }
  }

  public deleteTool(): void {
    this.toolService.deleteTool(this.tool.id).subscribe(() => {
      this.router.navigate(['settings', 'environment']).then((navigated) => {
        if (navigated) {
          this.snack.show(`Deleted tool - ${this.tool.name}`, 5000)
        }
      })
    })
  }

  // ---------------------------------------
  // UI HELPERS
  // ---------------------------------------
  public isTool(object: any): object is Tool {
    return 'product' in object
  }

  public onToggle(index: number, event: Event): void {
    const isOpen = (event.target as HTMLDetailsElement).open
    if (isOpen) this.openIndexes.add(index)
    else this.openIndexes.delete(index)
  }

  public onBreadcrumbClick(index: number): void {
    const item = this.breadcrumbs()[index]
    if (item.step === 'env') this.router.navigate(['settings', 'environment'])
  }

  public getReadableName(name: string): string {
    return name.replaceAll('_', ' ').replace(/([A-Z])/g, ' $1')
  }

  public getReadableFieldName(key: string): string {
    return fields[key].name
  }

  public getFieldDescription(key: string): string {
    return fields[key].description
  }

  public getFieldName(field: AbstractControl): string {
    return Object.keys(field.value)[0]
  }

  public applyDefaults(i): void {
    const controls = this.fieldMappingFields(i).controls
    controls.forEach((control) => {
      const defaultField = this.getFieldName(control)
      const toolCustom = toolFactory(this.toolForm.value.product)
        .toolDefaultCCMFieldMappings[defaultField]

      control.setValue({ [defaultField]: toolCustom ?? defaultField })
    })
  }

  public fieldMappingFields(i: number): FormArray {
    return this.fieldMappings().at(i).get('fields') as FormArray
  }

  public toolSpecificFields(i: number): FormArray {
    return this.fieldMappings().at(i).get('toolSpecific') as FormArray
  }

  public populateCategoryFields(
    selected: { category: string },
    i: number,
  ): void {
    const categoryKey = selected.category
    const fieldsArray = this.fieldMappings().at(i).get('fields') as FormArray

    fieldsArray.clear()

    this.categories[categoryKey].ccm_fields.forEach((field) => {
      fieldsArray.push(
        this.formBuilder.group({
          [field]: '',
        }),
      )
    })
  }
}
